import { Upload, X } from "lucide-react";
import { TextAreaField } from "./DesignRequestFormV3";
import { DesignRequestProjectSection } from "./DesignRequestProjectSection";
import { DesignRequestSkuSection } from "./DesignRequestSkuSection";
import {
  buildManufacturerOptions,
  manufacturerType,
  mergeSortedNames,
} from "./IntakeFormOptionUtils";
import "./DesignRequestForm.css";
import "./DesignRequestFormV4_2.css";

export const FORM_VERSION = "4.4";

const REQUIRED_ISR_NAMES = ["Jen Horstmann", "Leanne Darrington"];
const REQUIRED_OSR_NAMES = ["Nick Wilkins", "Servando Plascencia", "Tony Galletti"];

export const blankSku = () => ({
  sku_name: "",
  sku_number: "",
  design_type: "",
  conversion_type: "",
  conversion_date: "",
  previous_vendor_item_number: "",
  upc_code: "",
  number_of_colors: "",
  pasteurization_range: "Not Required",
  symbol: "None",
  specialty_ink_type: "None",
  specialty_ink_other: "",
  overvarnish_type: "",
  inside_varnish: "",
  pilot_requested: false,
  reference_collateral_sent: false,
  requesting_drawdowns: false,
  sample_same_as_contact: false,
  sample_recipient_name: "",
  sample_ship_to_address: "",
  sample_ship_to_street: "",
  sample_ship_to_city: "",
  sample_ship_to_state: "",
  sample_ship_to_zip: "",
  sample_recipient_email: "",
  sku_notes: "",
});

export const emptyDesignRequestForm = {
  customer_id: "",
  company_name: "",
  contact_name: "",
  contact_email: "",
  contact_phone: "",
  contact_street: "",
  contact_city: "",
  contact_state: "",
  contact_zip: "",
  additional_contacts: [],
  manufacturer_id: "",
  manufacturer_name: "",
  priority: "medium",
  deadline: "",
  gamer_po: "",
  customer_po: "",
  isr: "",
  osr: "",
  filler: "",
  brand: "",
  can_size: "",
  can_size_other: "",
  print_type: "",
  decoration_method: "",
  application_type: "",
  beverage_type: "",
  beer_type: "",
  can_finish: "",
  lid_type: "",
  comments: "",
};

export const DesignRequestFormFields = ({
  form,
  setForm,
  skuItems,
  setSkuItems,
  options = { customers: [], manufacturers: [], isr_list: [], osr_list: [], form_options: {} },
  attachments = [],
  onFilesSelected,
  onRemoveAttachment,
  uploadingFiles = false,
  artworkPoRequired = false,
}) => {
  const update = (name, value) => setForm((previous) => ({ ...previous, [name]: value }));
  const manufacturerOptions = buildManufacturerOptions(options, form.manufacturer_name);
  const isrNames = mergeSortedNames(options.isr_list, REQUIRED_ISR_NAMES);
  const osrNames = mergeSortedNames(options.osr_list, REQUIRED_OSR_NAMES);
  const selectedManufacturerType = manufacturerType(form.manufacturer_name);

  const handleManufacturerChange = (value) => {
    const manufacturer = manufacturerOptions.find((item) => item.value === value);
    const nextType = manufacturerType(manufacturer?.sourceName);
    setForm((previous) => ({
      ...previous,
      manufacturer_id: value,
      manufacturer_name: manufacturer?.sourceName || "",
    }));
    setSkuItems((previous) => previous.map((sku) => ({
      ...sku,
      inside_varnish: nextType === "ball"
        ? (String(sku.inside_varnish).toUpperCase() === "BPANI" ? sku.inside_varnish : "")
        : sku.inside_varnish,
      symbol: nextType === "ball" ? (sku.symbol || "None") : "",
    })));
  };

  const handleBeverageTypeChange = (value) => {
    setForm((previous) => ({
      ...previous,
      beverage_type: value,
      beer_type: value === "Beer" ? previous.beer_type : "",
    }));
  };

  const handlePrintTypeChange = (value) => {
    setForm((previous) => ({
      ...previous,
      print_type: value,
      decoration_method: String(value || "").toLowerCase().includes("digital") ? previous.decoration_method : "",
    }));
  };

  const updateSku = (index, field, value) => {
    setSkuItems((previous) => previous.map((sku, skuIndex) => (
      skuIndex === index ? { ...sku, [field]: value } : sku
    )));
  };

  const handleDesignTypeChange = (index, value) => {
    setSkuItems((previous) => previous.map((sku, skuIndex) => (
      skuIndex === index
        ? {
          ...sku,
          design_type: value,
          ...(value === "Revision" ? {} : {
            conversion_type: "",
            conversion_date: "",
            previous_vendor_item_number: "",
          }),
        }
        : sku
    )));
  };

  const addSku = () => setSkuItems((previous) => [...previous, blankSku()]);
  const duplicateSku = (index) => {
    setSkuItems((previous) => {
      const next = [...previous];
      next.splice(index + 1, 0, { ...previous[index] });
      return next;
    });
  };
  const removeSku = (index) => {
    setSkuItems((previous) => previous.length === 1 ? previous : previous.filter((_, skuIndex) => skuIndex !== index));
  };

  return (
    <>
      <div className="dof-version-banner">
        <strong>Form Version {FORM_VERSION}</strong>
        <span>Submission Revision 1</span>
      </div>

      <DesignRequestProjectSection
        form={form}
        update={update}
        options={options}
        manufacturerOptions={manufacturerOptions}
        handleManufacturerChange={handleManufacturerChange}
        isrNames={isrNames}
        osrNames={osrNames}
        handleBeverageTypeChange={handleBeverageTypeChange}
        handlePrintTypeChange={handlePrintTypeChange}
        artworkPoRequired={artworkPoRequired}
      />

      <DesignRequestSkuSection
        form={form}
        skuItems={skuItems}
        options={options}
        selectedManufacturerType={selectedManufacturerType}
        updateSku={updateSku}
        handleDesignTypeChange={handleDesignTypeChange}
        duplicateSku={duplicateSku}
        removeSku={removeSku}
        addSku={addSku}
      />

      <div className="dof-section-title">ARTWORK FILES</div>
      <div className="dof-contact-sub artwork-upload-panel">
        <label className="artwork-upload-button">
          <Upload size={18} />
          {uploadingFiles ? "Uploading..." : "Upload Artwork"}
          <input
            type="file"
            multiple
            disabled={uploadingFiles}
            accept=".pdf,.ai,.eps,.psd,.svg,.png,.jpg,.jpeg,.tif,.tiff,.zip"
            onChange={(event) => {
              if (onFilesSelected) onFilesSelected(Array.from(event.target.files || []));
              event.target.value = "";
            }}
          />
        </label>
        <p className="dof-note-text">Accepted: PDF, AI, EPS, PSD, SVG, PNG, JPG, TIFF, and ZIP. Files over 25 MB must be uploaded as a ZIP file.</p>
        {attachments.length > 0 && (
          <div className="artwork-upload-list">
            {attachments.map((attachment, index) => (
              <div key={attachment.id || `${attachment.filename}-${index}`} className="artwork-upload-item">
                <span>{attachment.filename}</span>
                <small>{attachment.size_bytes ? `${(attachment.size_bytes / 1024 / 1024).toFixed(2)} MB` : ""}</small>
                {onRemoveAttachment && (
                  <button type="button" onClick={() => onRemoveAttachment(index)} aria-label={`Remove ${attachment.filename}`}>
                    <X size={15} />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="dof-section-title">COMMENTS / REMARKS</div>
      <TextAreaField label="" value={form.comments} onChange={(value) => update("comments", value)} />
    </>
  );
};

export { InputField, SelectField, TextAreaField, CheckboxField } from "./DesignRequestFormV3";
